import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { nzTodayIso, displayDate } from "@/lib/dates";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export function AssignTaskDialog({ task, onClose, onSaved }) {
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState(new Set(task.assigned_user_ids || []));
  const [day, setDay] = useState(task.scheduled_date || nzTodayIso());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users", { __flSkipReadCache: true })
      .then((r) => setPeople((r.data || []).filter((u) => !u.suspended_at).sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(() => toast.error("Couldn't load your team"));
  }, []);

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const shown = people.filter((p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/job-tasks/${task.id}/assignees`, { user_ids: [...picked], date: day }, { __flOnlineOnly: true });
      toast.success(picked.size ? `Assigned to ${data.assigned_user_names.join(", ")} for ${displayDate(day)}` : "Task unassigned");
      onSaved(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Couldn't assign task");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-task-dialog">
        <DialogHeader>
          <DialogTitle>Assign task</DialogTitle>
          <DialogDescription>{task.title}. People you pick are booked onto this job for the day, and the task stays on their run-sheet until it's done.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assign-task-date">Day</Label>
            <Input id="assign-task-date" type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="assign-task-date" />
          </div>
          <div className="space-y-2">
            <Label>People {picked.size ? `(${picked.size})` : ""}</Label>
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team" data-testid="assign-task-search" /></div>
            <div className="max-h-64 overflow-auto rounded-md border border-border divide-y divide-border" data-testid="assign-task-people">
              {shown.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`assign-task-person-${p.id}`}>
                  <Checkbox checked={picked.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="text-sm flex-1">{p.name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{(p.role || "").replace(/_/g, " ")}</span>
                </label>
              ))}
              {!shown.length && <p className="p-3 text-sm text-muted-foreground">No one found.</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose} data-testid="assign-task-cancel">Cancel</Button>
          <Button disabled={saving || (picked.size > 0 && !day)} onClick={save} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="assign-task-save">
            {saving && <Loader2 className="animate-spin" />}{picked.size ? "Assign" : "Save (unassigned)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
